import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import {
  type Field,
  NumberUtils,
  TextWithTooltip,
} from '@pbt/pbt-ui-components'

import InfoList from '~/components/common/InfoList'
import TaxRateSelect from '~/components/common/inputs/TaxRateSelect'
import PuiSwitch from '~/components/common/PuiSwitch'
import VariationPricingPreTaxFees from '~/components/dashboard/admin/catalog/inventory/catalog/VariationPricingPreTaxFees'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Price } from '~/types'

interface VariationPricingProps {
  prices: Array<{
    fields: {
      discountAllowed: Field
      taxable: Field
      taxes: Field
    }
    price: Price
  }>
}

export const VariationPrice = ({ prices }: VariationPricingProps) => {
  const { t } = useTranslation('Common')
  const isCatalogControllingDiscount = useSelector(
    getFeatureToggle(FeatureToggle.DISCOUNT_ALLOWED_INVENTORY),
  )

  return (
    <Grid container spacing={3}>
      {prices.map(
        ({
          price: { id, price, minCharge, dispensingFee },
          fields: { discountAllowed, taxable, taxes },
        }) => (
          <Grid container item key={id}>
            <InfoList
              items={[
                {
                  name: t('Common:CLIENT_PRICE'),
                  value: NumberUtils.formatMoney(price ?? 0),
                },
                {
                  name: t('Common:MINIMUM_CHARGE'),
                  value: NumberUtils.formatMoney(minCharge ?? 0),
                },
                {
                  name: t('Common:DISPENSING_FEE'),
                  value: NumberUtils.formatMoney(dispensingFee ?? 0),
                },
              ]}
            />
            <Grid container item flexDirection="column" mt={2}>
              <PuiSwitch
                disabled={isCatalogControllingDiscount}
                field={discountAllowed}
                label={
                  isCatalogControllingDiscount ? (
                    <TextWithTooltip
                      tooltipText={t(
                        'Tooltips:DISCOUNT_CONTROLLED_BY_CATALOG_TEAM',
                      )}
                    >
                      {discountAllowed.label}
                    </TextWithTooltip>
                  ) : (
                    discountAllowed.label
                  )
                }
              />
            </Grid>
            <Grid
              container
              item
              alignItems="flex-end"
              columnSpacing={1}
              wrap="nowrap"
            >
              <Grid item>
                <PuiSwitch field={taxable} label={taxable.label} />
              </Grid>
              {taxable.value && (
                <Grid item width={300}>
                  <TaxRateSelect field={taxes} taxableField={taxable} />
                </Grid>
              )}
              {taxable.value && <VariationPricingPreTaxFees field={taxes} />}
            </Grid>
          </Grid>
        ),
      )}
    </Grid>
  )
}
